import { useContext, useEffect } from 'react';
import { AppContext } from 'Context';
import { peekPostPurchaseReturn, clearPostPurchaseReturn } from 'Utils/postPurchaseReturn';
import { navigateTo } from '../../navigationHelper';

// Shared logic for the post-purchase success popup, used by both the classic
// (SuccessPopUp) and the new-UI (SuccessPopUpV2) variants so the two never drift
// apart on behaviour — only on presentation.

// Adds `duration` days to the purchase datetime and formats it as e.g.
// "12th October 2026". Returns '' when the inputs are missing/unparseable.
export const formatSuccessDate = (dateString, duration) => {
  if (!dateString) return '';
  const startDate = new Date(dateString);
  const days = parseInt(duration, 10);
  if (Number.isFinite(days)) startDate.setDate(startDate.getDate() + days);
  if (Number.isNaN(startDate.getTime())) return '';

  const day = startDate.getDate();
  const month = startDate.toLocaleString('en-US', { month: 'long' });
  const year = startDate.getFullYear();
  const suffix = day % 10 === 1 && day !== 11
    ? 'st'
    : day % 10 === 2 && day !== 12
      ? 'nd'
      : day % 10 === 3 && day !== 13
        ? 'rd'
        : 'th';
  return `${day}${suffix} ${month} ${year}`;
};

// The amount charged, if the purchase response carried one. Hidden otherwise.
export const formatAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
};

export const useSuccessPopup = ({
  expressData,
  onHide,
  paymentStatus,
  paymentNavigation,
  setOpenPaymentLoader,
  isSwitchSuccess,
}) => {
  const { setFirstTimeOnlyChecked } = useContext(AppContext);

  useEffect(() => {
    setOpenPaymentLoader(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSuccess = paymentStatus === 'success' || paymentStatus === 'affliate';
  const isTrial = !!expressData?.is_trial;
  const endDate = formatSuccessDate(
    expressData?.datetime,
    isTrial ? expressData?.plan_trial_duration : expressData?.plan_duration,
  );
  const amountText = formatAmount(expressData?.value);
  // Lifetime plans (36500 days) and trials have no "next renewal" line.
  const showRenewal = isSuccess && !isTrial && expressData?.plan_duration !== '36500';

  // Close/Exit. Preserves the exact navigation the classic popup used:
  // switch-plan success leaves navigation to expressCheckout; otherwise route by
  // paymentStatus (affiliate -> manage users, success -> dashboard, else billing).
  const handleClose = (e) => {
    if (onHide) {
      onHide(e);
      setFirstTimeOnlyChecked(false);
    }
    if (isSwitchSuccess) return;
    if (paymentStatus) {
      if (paymentStatus === 'affliate' || paymentStatus === 'affliate_error') {
        paymentNavigation('/settings/manage-users');
      } else if (paymentStatus === 'success') {
        // If the user reached the plans/trial flow from the upload blocker,
        // send them back to the exact page they were uploading from so they can
        // continue. Otherwise the normal post-purchase destination.
        const returnUrl = peekPostPurchaseReturn();
        if (returnUrl) {
          clearPostPurchaseReturn();
          paymentNavigation(returnUrl);
        } else {
          paymentNavigation('/dashboard');
        }
      } else {
        paymentNavigation('/settings/billings-and-payments');
      }
    }
  };

  const handleRetry = () => {
    if (onHide) onHide();
    navigateTo('/plans');
  };

  return {
    isSuccess,
    isTrial,
    endDate,
    amountText,
    showRenewal,
    handleClose,
    handleRetry,
  };
};
